import type { BootFallbackGridSpec, BootGridPresenterPort } from "../composition/ports/BootGridPresenterPort";
import type { Log } from "@slot/presentation";

/** The grid as the boot paints it: reset the mechanic's presentation state, paint an encoded snapshot without motion. */
export interface GridPaintPort {
  reset(): void;
  paint(encodedGridSnapshot: string): Promise<void>;
}

/**
 * The boot grid: the fallback snapshot painted before the first round and again after a failed one
 * (a rejected spin, a transport error). "Ready" resolves once the first paint has been rendered.
 */
export class BootGridPresenter implements BootGridPresenterPort {
  private readonly grid: GridPaintPort;
  private readonly waitForNextRenderedFrame: () => Promise<void>;
  private readonly log: Log;
  private readonly ready: Promise<void>;
  private markReady: () => void = () => {};

  public constructor(input: { grid: GridPaintPort; waitForNextRenderedFrame: () => Promise<void>; log: Log }) {
    this.grid = input.grid;
    this.waitForNextRenderedFrame = input.waitForNextRenderedFrame;
    this.log = input.log;
    this.ready = new Promise<void>((resolve) => {
      this.markReady = resolve;
    });
  }

  public whenInitialGridReady(): Promise<void> {
    return this.ready;
  }

  public async showInitialGrid(spec: BootFallbackGridSpec): Promise<void> {
    this.log(`[BootGrid] painting the initial grid (reelSet=${spec.reelSetIdentifier ?? "boot"})`);
    await this.grid.paint(spec.encodedGridSnapshot);
    await this.waitForNextRenderedFrame();
    this.markReady();
    this.log("[BootGrid] initial grid ready");
  }

  public async restoreFallbackGrid(spec: BootFallbackGridSpec): Promise<void> {
    this.log("[BootGrid] reset presentation state");
    this.grid.reset();
    this.log(`[BootGrid] painting the fallback grid (reelSet=${spec.reelSetIdentifier ?? "default"})`);
    await this.grid.paint(spec.encodedGridSnapshot);
    await this.waitForNextRenderedFrame();
    this.log("[BootGrid] fallback grid restored");
  }
}
